import { useEffect, useState } from "react";

import type { BlogHeading } from "@/features/blog/blog-utils";
import { cn } from "@/common/utils";
import { Eyebrow } from "@/landing/system/layout";

/**
 * Sticky list of the post's h2 sections. The section under the header is
 * marked with the accent rule.
 */
export function TableOfContents({
  headings,
  label,
  className,
}: {
  headings: BlogHeading[];
  label: string;
  className?: string;
}) {
  const [activeId, setActiveId] = useState<string>();

  useEffect(() => {
    let frame = 0;
    const update = () => {
      frame = 0;
      // A heading counts as current once it passes a line just under the header.
      const line = 140;
      let current: string | undefined;
      for (const heading of headings) {
        const element = document.getElementById(heading.id);
        if (element && element.getBoundingClientRect().top <= line) {
          current = heading.id;
        }
      }
      setActiveId(current);
    };
    const onScroll = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      cancelAnimationFrame(frame);
    };
  }, [headings]);

  if (headings.length < 2) return null;

  return (
    <nav
      aria-label={label}
      className={cn(
        "sticky top-[calc(var(--header-height)+32px)] max-h-[calc(100vh-var(--header-height)-64px)] self-start overflow-y-auto",
        className,
      )}
    >
      <Eyebrow className="mb-4">{label}</Eyebrow>
      <ol className="m-0 list-none border-l border-(--g-rule-soft) p-0">
        {headings.map((heading) => (
          <li key={heading.id}>
            <a
              href={`#${heading.id}`}
              aria-current={activeId === heading.id ? "location" : undefined}
              className={cn(
                "-ml-px block border-l py-[7px] pl-4 text-[13.5px] leading-[1.4] no-underline transition-colors",
                activeId === heading.id
                  ? "border-(--g-accent) text-(--g-ink)"
                  : "border-transparent text-(--g-ink-3) hover:text-(--g-ink)",
              )}
            >
              {heading.text}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
}
